import { useCallback, useEffect, useRef, useState } from "react";

/**
 * The single press-feedback system: the codes currently shown depressed, fed by a row
 * click, a cap or zone click, a readout-chip click, or a physical keypress, each superseding the last
 * through a sequence guard so a quick second press keeps its own 240ms depress alive.
 */
export function usePressFeedback() {
  const [pressed, setPressed] = useState<{ codes: string[]; seq: number } | null>(null);
  const pressSeq = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // depress the given slots for 240ms. A later press supersedes an earlier one through the seq guard,
  // so a quick second press keeps its own depress alive. The pending timer is cleared first (the seq
  // guard already neutralises a stale callback; clearing stops timers piling up) and on unmount, so a
  // press within 240ms of teardown never fires setPressed on a gone component.
  const pressFlash = useCallback((slots: string[]) => {
    pressSeq.current += 1;
    const seq = pressSeq.current;
    setPressed({ codes: slots, seq });
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setPressed((p) => (p && p.seq === seq ? null : p)), 240);
  }, []);
  useEffect(() => () => clearTimeout(timer.current), []);
  return { pressed, pressFlash };
}
